import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Puzzle } from '../types';
import { puzzleAPI } from '../api';
import { formatDate, formatLongDate } from '../utils/format';
import {
  colors,
  fonts,
  radius,
  shadow,
  sectionLabel,
  focusRing,
} from '../theme';

const ListContainer = styled.div`
  padding: 32px 20px 20px;
  max-width: 460px;
  margin: 0 auto;
`;

const Masthead = styled.div`
  text-align: center;
  margin-bottom: 26px;
`;

const PenguinLogo = styled.img`
  height: 96px;
  width: 96px;
  object-fit: contain;
  margin-bottom: 4px;
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-size: 30px;
  margin: 0;
  letter-spacing: -0.015em;
`;

/* A thin double rule under the masthead — a small nod to newsprint. */
const Rule = styled.div`
  margin: 12px auto 0;
  height: 3px;
  max-width: 180px;
  border-top: 1px solid ${colors.ruleStrong};
  border-bottom: 1px solid ${colors.ruleStrong};
`;

const DailyCard = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: ${colors.card};
  border: 1px solid ${colors.ruleStrong};
  border-left: 4px solid ${colors.accent};
  border-radius: ${radius.md};
  box-shadow: ${shadow.panel};
  padding: 16px 18px;
  margin-bottom: 28px;
  cursor: pointer;
  transition: background 0.12s ease;
  ${focusRing}

  &:hover {
    background: ${colors.cardMuted};
  }
`;

const DailyLabel = styled.div`
  ${sectionLabel}
  color: ${colors.accent};
  margin-bottom: 2px;
`;

const DailyDate = styled.div`
  font-family: ${fonts.display};
  font-size: 20px;
  font-weight: 700;
  color: ${colors.ink};
`;

const DailyHint = styled.div`
  font-size: 13px;
  color: ${colors.inkFaint};
  margin-top: 2px;
`;

const SectionTitle = styled.h2`
  ${sectionLabel}
  margin: 0 0 8px;
`;

const PuzzleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${colors.card};
  border: 1px solid ${colors.rule};
  border-radius: ${radius.md};
  box-shadow: ${shadow.panel};
  overflow: hidden;
`;

const PuzzleRow = styled.tr`
  cursor: pointer;
  border-bottom: 1px solid ${colors.rule};
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.cardMuted};
  }
`;

const PuzzleCell = styled.td`
  padding: 9px 14px;
  font-size: 14px;
  color: ${colors.ink};
`;

const PuzzleDateCell = styled(PuzzleCell)`
  color: ${colors.inkFaint};
  font-size: 13px;
  text-align: right;
  white-space: nowrap;
`;

const Message = styled.div`
  text-align: center;
  padding: 56px 20px;
  font-family: ${fonts.display};
  font-size: 16px;
  color: ${colors.inkFaint};
`;

export const PuzzleList: React.FC = () => {
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [dailyPuzzle, setDailyPuzzle] = useState<Puzzle | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadPuzzles = async () => {
      // The daily fetch can fail (upstream hiccup); the list should still render.
      const [allPuzzles, daily] = await Promise.all([
        puzzleAPI.getPuzzles().catch(error => {
          console.error('Error loading puzzles:', error);
          return [] as Puzzle[];
        }),
        puzzleAPI.ensureTodayPuzzle().catch(error => {
          console.error("Error loading today's puzzle:", error);
          return null;
        }),
      ]);

      setPuzzles(Array.isArray(allPuzzles) ? allPuzzles : []);
      setDailyPuzzle(daily);
      setLoading(false);
    };

    loadPuzzles();
  }, []);

  if (loading) {
    return <Message>Loading puzzles…</Message>;
  }

  const pastPuzzles = [...puzzles]
    .filter(puzzle => puzzle.id !== dailyPuzzle?.id)
    .sort(
      (a, b) =>
        new Date(b.daily_date || b.created_at).getTime() -
        new Date(a.daily_date || a.created_at).getTime()
    );

  return (
    <ListContainer>
      <Masthead>
        <PenguinLogo src="/penguin.png" alt="" />
        <Title>Mini Crossword</Title>
        <Rule />
      </Masthead>

      {dailyPuzzle && (
        <DailyCard onClick={() => navigate(`/puzzle/${dailyPuzzle.id}`)}>
          <DailyLabel>Today's puzzle</DailyLabel>
          <DailyDate>
            {formatLongDate(dailyPuzzle.daily_date || dailyPuzzle.created_at)}
          </DailyDate>
          <DailyHint>Five across, five down. Beat the clock.</DailyHint>
        </DailyCard>
      )}

      {pastPuzzles.length === 0 ? (
        <Message>No other puzzles yet.</Message>
      ) : (
        <>
          <SectionTitle>Past puzzles</SectionTitle>
          <PuzzleTable>
            <tbody>
              {pastPuzzles.map(puzzle => (
                <PuzzleRow key={puzzle.id} onClick={() => navigate(`/puzzle/${puzzle.id}`)}>
                  <PuzzleCell>{puzzle.title}</PuzzleCell>
                  <PuzzleDateCell>
                    {formatDate(puzzle.daily_date || puzzle.created_at)}
                  </PuzzleDateCell>
                </PuzzleRow>
              ))}
            </tbody>
          </PuzzleTable>
        </>
      )}
    </ListContainer>
  );
};

export default PuzzleList;
